import { useState } from 'react'
import type { ClientIntegration, SettingsCardProps } from '../index'
import { ApiError, req, type IntegrationsStatus } from '../../lib/api'
import { Button } from '../../components/ui/button'
import { Field } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { Modal, ModalActions, ModalLede, ModalTitle } from '../../components/ui/modal'
import { Note } from '../../components/ui/note'
import { Segmented, SegmentedItem } from '../../components/ui/segmented'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { BrandMark } from '@/components/ui/brand-mark'
import { DisconnectButton, IntegrationCard } from '../ui'
import { HeatmapMenuItem } from './HeatmapMenuItem'
import { HeatmapOverlay } from './HeatmapOverlay'
import { setPostHogConnected } from './heatmaps'
import { useT } from '@/lib/i18n'

interface PostHogStatus {
  connected: boolean
  host?: string
  accountName?: string
  connectedAt?: number
}

type Region = 'us' | 'eu' | 'self'
type Project = { id: string; name: string }

function statusOf(status: IntegrationsStatus): PostHogStatus {
  return (status.posthog ?? { connected: false }) as PostHogStatus
}

function PostHogTile({ size = 24, className }: { size?: number; className?: string }) {
  return <BrandMark brand="posthog" size={size} className={className} />
}

function failure(error: unknown, fallback: string) {
  return error instanceof ApiError ? String(error.body.error ?? fallback) : fallback
}

function PostHogSettingsCard({ status, busy, setBusy, setStatus, requestDisconnect, showToast }: SettingsCardProps) {
  const posthog = statusOf(status)
  const [open, setOpen] = useState(false)
  const [region, setRegion] = useState<Region>('us')
  const [selfHost, setSelfHost] = useState('')
  const [key, setKey] = useState('')
  const [projects, setProjects] = useState<Project[] | null>(null)
  const [projectId, setProjectId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const host = region === 'self' ? selfHost.trim() : region
  const t = useT()

  function close() {
    if (busy) return
    reset()
  }

  function reset() {
    setOpen(false)
    setKey('')
    setProjects(null)
    setProjectId('')
    setError(null)
  }

  /* any change to the address or key invalidates the projects it listed */
  function edited() {
    setProjects(null)
    setProjectId('')
    setError(null)
  }

  async function findProjects() {
    if (busy || !host || !key.trim()) return
    setBusy(true)
    setError(null)
    try {
      const found = await req<{ projects: Project[] }>('/api/integrations/posthog/projects', {
        method: 'POST',
        body: JSON.stringify({ host, key: key.trim() }),
      })
      setProjects(found.projects)
      setProjectId(found.projects.length === 1 ? (found.projects[0]?.id ?? '') : '')
    } catch (err) {
      setError(failure(err, t('automations.posthogReachFailed')))
    } finally {
      setBusy(false)
    }
  }

  async function connect() {
    if (busy || !projectId) return
    setBusy(true)
    setError(null)
    try {
      setStatus(
        await req<IntegrationsStatus>('/api/integrations/posthog/connect', {
          method: 'POST',
          body: JSON.stringify({ host, key: key.trim(), projectId }),
        }),
      )
      setPostHogConnected(true)
      showToast(posthog.connected ? t('automations.posthogProjectChanged') : t('automations.posthogConnected'))
      setBusy(false)
      /* `busy` in this closure is still the true it started with */
      reset()
    } catch (err) {
      setError(failure(err, t('automations.posthogConnectFailed')))
      setBusy(false)
    }
  }

  return (
    <>
      <IntegrationCard
        tile={<PostHogTile size={36} />}
        name="PostHog"
        detail={
          posthog.connected ? (posthog.accountName ?? t('automations.posthogProject')) : t('automations.posthogPitch')
        }
        connected={posthog.connected}
        footnote={posthog.connected ? t('automations.posthogFootnote') : ''}
        actions={
          <>
            <Button
              variant={posthog.connected ? 'bare' : 'default'}
              size="sm"
              disabled={busy}
              onClick={() => setOpen(true)}
            >
              {posthog.connected ? t('automations.changeProject') : t('automations.connect')}
            </Button>
            {posthog.connected && <DisconnectButton disabled={busy} onClick={requestDisconnect} />}
          </>
        }
      />
      <Modal open={open} onClose={close} size="md">
        <ModalTitle>
          {posthog.connected ? t('automations.changePosthogProject') : t('automations.connectPosthog')}
        </ModalTitle>
        <ModalLede>{t('automations.posthogLede')}</ModalLede>
        <form
          className="mt-5 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            void (projects ? connect() : findProjects())
          }}
        >
          <Field label={t('automations.posthogInstance')} labelVariant="form">
            <Segmented
              aria-label={t('automations.posthogInstance')}
              value={region}
              onValueChange={(next) => {
                setRegion(next as Region)
                edited()
              }}
            >
              <SegmentedItem value="us">{t('automations.posthogUsCloud')}</SegmentedItem>
              <SegmentedItem value="eu">{t('automations.posthogEuCloud')}</SegmentedItem>
              <SegmentedItem value="self">{t('automations.posthogSelfHosted')}</SegmentedItem>
            </Segmented>
          </Field>
          {region === 'self' && (
            <Field label={t('automations.posthogAddress')} labelVariant="form" htmlFor="posthog-host">
              <Input
                id="posthog-host"
                variant="well"
                placeholder="https://posthog.example.com"
                value={selfHost}
                disabled={busy}
                onChange={(event) => {
                  setSelfHost(event.target.value)
                  edited()
                }}
              />
            </Field>
          )}
          <Field
            label={t('automations.posthogKeyLabel')}
            labelVariant="form"
            htmlFor="posthog-key"
            hint={
              <>
                {t('automations.posthogKeyBefore')}{' '}
                <a
                  href={`${region === 'eu' ? 'https://eu.posthog.com' : region === 'us' ? 'https://us.posthog.com' : selfHost.trim() || 'https://us.posthog.com'}/settings/user-api-keys`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  {t('automations.posthogKeyLink')}
                </a>{' '}
                {t('automations.posthogKeyAfter')}
              </>
            }
          >
            <Input
              id="posthog-key"
              type="password"
              autoComplete="off"
              variant="well"
              placeholder="phx_…"
              value={key}
              disabled={busy}
              onChange={(event) => {
                setKey(event.target.value)
                edited()
              }}
            />
          </Field>
          {projects && (
            <Field label={t('automations.posthogProjectLabel')} labelVariant="form">
              <Select value={projectId} disabled={busy} onValueChange={setProjectId}>
                <SelectTrigger aria-label={t('automations.posthogProjectLabel')}>
                  <SelectValue placeholder={t('automations.posthogPickProject')} />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {error && (
            <Note role="alert" tone="error">
              {error}
            </Note>
          )}
          <ModalActions>
            <Button type="button" variant="bare" disabled={busy} onClick={close}>
              {t('common.cancel')}
            </Button>
            {projects ? (
              <Button type="submit" variant="primary" disabled={busy || !projectId}>
                {busy ? t('automations.checking') : t('automations.connect')}
              </Button>
            ) : (
              <Button type="submit" variant="primary" disabled={busy || !host || !key.trim()}>
                {busy ? t('automations.checking') : t('automations.continue')}
              </Button>
            )}
          </ModalActions>
        </form>
      </Modal>
    </>
  )
}

export const posthogIntegration: ClientIntegration = {
  id: 'posthog',
  name: 'PostHog',
  Tile: PostHogTile,
  automationPull: {
    blurb: 'Read how visitors use a page before an agent redesigns it.',
    subtitle: 'Page insights',
    placeholder: 'https://example.com/pricing',
    field: 'url',
  },
  SettingsCard: PostHogSettingsCard,
  StageOverlay: HeatmapOverlay,
  FrameMenuItem: HeatmapMenuItem,
  isConnected: (status) => statusOf(status).connected,
  disconnect: async () => {
    const status = await req<IntegrationsStatus>('/api/integrations/posthog', { method: 'DELETE' })
    setPostHogConnected(false)
    return status
  },
  disconnectCopy: 'automations.posthogDisconnectCopy',
}
